const RELOADED_AT = 'page-chunk-reloaded-at';
/** Long enough to tell a genuinely broken chunk from a stale tab reloading once. */
const RETRY_WINDOW_MS = 10_000;

const readStamp = () => {
  try {
    return Number(sessionStorage.getItem(RELOADED_AT) ?? 0);
  } catch {
    return 0;
  }
};

const writeStamp = (at: number) => {
  try {
    sessionStorage.setItem(RELOADED_AT, String(at));
  } catch {
    // Storage blocked: the reload still happens, it just cannot be rate-limited.
  }
};

/**
 * Loads a lazily split page, surviving a deploy that happened while the tab was
 * open.
 *
 * Every build renames its chunks by content hash. A tab opened before a deploy
 * still holds the old index.html, so the next page it navigates to asks for a
 * file that no longer exists, and the import fails - which React Router shows
 * as a full-screen "Unexpected Application Error".
 *
 * One reload fetches the current index.html and its current chunk names. A
 * second failure inside the window is a real problem rather than a stale tab,
 * so it is thrown instead of reloading forever.
 *
 * `destination` is where the navigation was headed. The import fails before
 * the URL changes, so a plain reload would reload the page being left: click
 * Reports, land back on Workspaces.
 */
export async function importPage<T>(
  load: () => Promise<T>,
  destination?: () => string | undefined,
): Promise<T> {
  try {
    return await load();
  } catch (error) {
    const now = Date.now();
    if (now - readStamp() < RETRY_WINDOW_MS) throw error;

    writeStamp(now);
    const target = destination?.();
    if (target) window.location.assign(target);
    else window.location.reload();
    // The page is going away; never resolve, so nothing renders in between.
    return new Promise<T>(() => {});
  }
}
